import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, Star, MessageCircle, ChevronRight, Package } from 'lucide-react';
import { API, CIGAR_PLACEHOLDER } from '../config/constants';
import BottomNav from '../components/BottomNav';

const PublicProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('ratings');

  useEffect(() => {
    fetchProfile();
  }, [userId]);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/users/${userId}/profile`);
      const data = res.data;
      setProfile({
        ...data.user,
        ratings_count: data.stats?.ratings_count || 0,
        comments_count: data.stats?.comments_count || 0,
        cigars_added_count: data.stats?.cigars_added_count || 0,
        cigars_rated: data.cigars_rated || [],
        comments: data.comments || [],
        cigars_added: data.cigars_added || []
      });
    } catch (error) {
      console.error('Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center">
        <p className="text-white">Loading...</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-[#0B0B0C] flex flex-col">
        <div className="flex items-center p-4 border-b border-white/10">
          <button onClick={() => navigate(-1)} className="text-white mr-4">
            <X size={24} />
          </button>
          <h1 className="playfair text-xl text-white flex-1 text-center pr-8">Profile</h1>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-white/50">User not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0C] flex flex-col pb-24">
      {/* Header */}
      <div className="flex items-center p-4 border-b border-white/10">
        <button onClick={() => navigate(-1)} className="text-white mr-4">
          <X size={24} />
        </button>
        <h1 className="playfair text-xl text-white flex-1 text-center pr-8">Profile</h1>
      </div>

      {/* Profile Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Profile Picture and Name */}
        <div className="flex flex-col items-center p-6 pb-4">
          {profile.profile_pic ? (
            <img 
              src={profile.profile_pic} 
              alt={profile.username}
              className="w-20 h-20 rounded-full object-cover mb-3"
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center text-white font-bold text-2xl mb-3">
              {profile.username?.charAt(0).toUpperCase() || 'U'}
            </div>
          )}
          <h2 className="text-xl font-bold text-white">{profile.username}</h2>
        </div>

        {/* Stats Tabs */}
        <div className="flex border-b border-white/10">
          <button
            onClick={() => setActiveTab('ratings')}
            className={`flex-1 py-3 flex items-center justify-center gap-1 transition-colors ${
              activeTab === 'ratings' 
                ? 'text-amber-500 border-b-2 border-amber-500' 
                : 'text-white/50'
            }`}
          >
            <Star size={16} />
            <span className="font-medium text-sm">{profile.ratings_count} Ratings</span>
          </button>
          <button
            onClick={() => setActiveTab('comments')}
            className={`flex-1 py-3 flex items-center justify-center gap-1 transition-colors ${
              activeTab === 'comments' 
                ? 'text-amber-500 border-b-2 border-amber-500' 
                : 'text-white/50'
            }`}
          >
            <MessageCircle size={16} />
            <span className="font-medium text-sm">{profile.comments_count} Comments</span>
          </button>
          <button
            onClick={() => setActiveTab('added')}
            className={`flex-1 py-3 flex items-center justify-center gap-1 transition-colors ${
              activeTab === 'added' 
                ? 'text-amber-500 border-b-2 border-amber-500' 
                : 'text-white/50'
            }`}
          >
            <Package size={16} />
            <span className="font-medium text-sm">{profile.cigars_added_count} Added</span>
          </button>
        </div>

        {/* Content based on tab */}
        <div className="p-4 space-y-3">
          {activeTab === 'ratings' && (
            <>
              {profile.cigars_rated.length === 0 ? (
                <p className="text-white/50 text-center py-8">No ratings yet</p>
              ) : (
                profile.cigars_rated.map((item, idx) => (
                  <div 
                    key={idx}
                    onClick={() => navigate(`/cigar/${item.cigar?.id}`)}
                    className="flex items-center gap-3 p-3 bg-white/5 rounded-xl cursor-pointer hover:bg-white/10 transition-colors"
                  >
                    {item.cigar?.image_url ? (
                      <img 
                        src={item.cigar.image_url} 
                        alt={item.cigar?.name}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-amber-700 to-amber-900 flex items-center justify-center">
                        <span className="text-white/50 text-xs">No img</span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{item.cigar?.name || 'Unknown Cigar'}</p>
                      <p className="text-white/50 text-sm">{item.cigar?.brand || ''}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="bg-amber-500/20 px-3 py-1 rounded-full flex items-center gap-1">
                        <Star className="text-amber-500" size={14} fill="#f59e0b" />
                        <span className="text-amber-500 font-bold">{item.rating}</span>
                      </div>
                      <ChevronRight className="text-white/30" size={20} />
                    </div>
                  </div>
                ))
              )}
            </>
          )}

          {activeTab === 'comments' && (
            <>
              {profile.comments.length === 0 ? (
                <p className="text-white/50 text-center py-8">No comments yet</p>
              ) : (
                profile.comments.map((comment, idx) => (
                  <div 
                    key={idx}
                    onClick={() => navigate(`/cigar/${comment.cigar_id}`)}
                    className="p-3 bg-white/5 rounded-xl cursor-pointer hover:bg-white/10 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <p className="text-white/50 text-xs">{formatDate(comment.created_at)}</p>
                    </div>
                    <p className="text-white text-sm line-clamp-3">{comment.text}</p>
                    <div className="flex items-center gap-1 mt-2 text-amber-500 text-xs">
                      <span>View cigar</span>
                      <ChevronRight size={14} />
                    </div>
                  </div>
                ))
              )}
            </>
          )}

          {activeTab === 'added' && (
            <>
              {profile.cigars_added.length === 0 ? (
                <p className="text-white/50 text-center py-8">No cigars added yet</p>
              ) : (
                profile.cigars_added.map((cigar, idx) => (
                  <div 
                    key={idx}
                    onClick={() => navigate(`/cigar/${cigar.id}`)}
                    className="flex items-center gap-3 p-3 bg-white/5 rounded-xl cursor-pointer hover:bg-white/10 transition-colors"
                  >
                    <img 
                      src={cigar.image_url || CIGAR_PLACEHOLDER} 
                      alt={cigar.name}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{cigar.name}</p>
                      <p className="text-white/50 text-sm">{cigar.brand}</p>
                      <p className="text-white/30 text-xs">{cigar.strength} · {cigar.origin}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {cigar.average_rating > 0 && (
                        <div className="bg-amber-500/20 px-2 py-1 rounded-full flex items-center gap-1">
                          <Star className="text-amber-500" size={12} fill="#f59e0b" />
                          <span className="text-amber-500 font-bold text-sm">{cigar.average_rating?.toFixed(1)}</span>
                        </div>
                      )}
                      <ChevronRight className="text-white/30" size={20} />
                    </div>
                  </div>
                ))
              )}
            </>
          )}
        </div>
      </div>
      
      <BottomNav />
    </div>
  );
};

export default PublicProfilePage;
